import styled from '@emotion/styled';
import { ClickAwayListener, Box } from '@mui/material';
import { PencilSimple } from '@phosphor-icons/react';
import { CellInput } from 'features/misc/table';
import { DotsMenuButton } from 'features/misc/table/components/DotsMenuButton';
import { TableTooltip } from '../../TableTooltip';
import { useEscapeToClose } from '../../../hooks';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { clearEditOnSuccess } from './inlineEditUtils';
import { DescriptionCellContainer, DescriptionText } from '../../tableStyles';
import type { EditableDescriptionCellProps } from '../../../components/types';

/**
 * EditableDescriptionCell - A self-contained inline-editable description cell for tables
 *
 * Edit state is derived only from props (editableRowIndex, editableColumnId). On click
 * calls onCellEdit(context) so the parent sets editableState; on click-away or save
 * calls onClickAway so the parent clears it. No dependency on EditableCell.
 */
export const EditableDescriptionCell = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  placeholder = 'Enter value...',
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
}: EditableDescriptionCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClickAway =
    onClickAwayProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const handleClick = () => {
    if (!isEditing) {
      onCellEdit?.(context);
    }
  };

  // While the leave-confirmation dialog is open it steals focus / a navigating click fires
  // click-away. Skip closing so the cell stays in edit mode until the user answers; the
  // navigation guard owns this interaction (DL-60097).
  // Skip the editor's commit while a navigation is being confirmed (flag raised synchronously
  // by the guard — on dialog open, or in a capture-phase pointerdown before this blur when an
  // in-app nav link is clicked) so the edit is never sent and "Leave" / Esc discard it (DL-60097).
  const shouldSkipCommit = useShouldSkipInlineCommit();

  const handleClickAway = () => {
    if (shouldSkipCommit()) return;
    onClickAway?.();
  };

  useEscapeToClose(isEditing, handleClickAway);

  const readModeContent = (
    <DescriptionCellContainer>
      <DescriptionText>{value || ''}</DescriptionText>
    </DescriptionCellContainer>
  );

  const showDotsMenu = context.row.original.id !== -1;

  return (
    <>
      <CellContainer
        onClick={handleClick}
        isEditable
        tooltipTitle={isEditing ? undefined : value || ''}
      >
        {isEditing ? (
          <ClickAwayListener onClickAway={handleClickAway}>
            <EditorContainer>
              <CellInput
                value={value || ''}
                placeholder={placeholder}
                onChange={newValue => {
                  // CellInput commits on blur — opening the leave dialog (or clicking an in-app
                  // nav link) raises the skip-flag first, so "Leave" / Esc discard instead of
                  // persisting (DL-60097).
                  if (shouldSkipCommit()) return;
                  const result = onUpdate(newValue);
                  clearEditOnSuccess(result ?? undefined, () =>
                    setEditableState({ rowIndex: null, columnId: null })
                  );
                }}
              />
            </EditorContainer>
          </ClickAwayListener>
        ) : (
          readModeContent
        )}
      </CellContainer>
      {showDotsMenu && !isEditing ? (
        <DotsMenuButtonWrapper>
          <DotsMenuButton
            menuOptions={[
              {
                label: 'Edit',
                icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
                onClick: () => {
                  onCellEdit?.(context);
                },
              },
            ]}
          />
        </DotsMenuButtonWrapper>
      ) : null}
    </>
  );
};

// ============================================================================
// Styled Components
// ============================================================================

const EditorContainer = styled.div`
  width: 100%;
  min-height: 30px;
`;

const CellContentContainer = styled.div<{ isEditable?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
  min-height: 40px;
  width: 100%;
`;

const DotsMenuButtonWrapper = styled(Box)`
  max-width: 18px;
`;

const CellContainer = ({
  children,
  tooltipTitle,
  onClick,
  isEditable,
}: {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  onClick?: () => void;
  isEditable?: boolean;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <CellContentContainer onClick={onClick} isEditable={isEditable}>
        {children}
      </CellContentContainer>
    </TableTooltip>
  ) : (
    <CellContentContainer onClick={onClick} isEditable={isEditable}>
      {children}
    </CellContentContainer>
  );
};
